/**
 * Tytuły do potrącenia z wynagrodzenia (Etap 33, ADR-0034).
 *
 * Karta w zakładce „Wynagrodzenie" akt pracownika. Zajęcie wynagrodzenia to
 * pismo, które przychodzi do pracodawcy — więc tytuł wpisują kadry, a granice
 * z art. 87 k.p. i kwotę wolną z art. 87(1) liczy silnik przy naliczeniu.
 * Gdy potrącenie nie zmieści się w granicach, pasek i lista płac mówią, która
 * granica zadziałała.
 *
 * Etap 34 dopisał tu odbiorcę przelewu (art. 881 § 3 k.p.c.) i dzień pierwszej
 * wypłaty: bez rachunku odbiorcy potrącenie policzy się, ale nie wyjdzie
 * z pliku przelewów — i to karta musi powiedzieć wprost, zamiast milczeć.
 *
 * Etap 35 dodał oświadczenie z art. 882 § 1 k.p.c. z tygodniowym terminem od
 * doręczenia wezwania. Termin liczy serwer, a karta go pokazuje: za brak
 * oświadczenia art. 886 § 1 przewiduje grzywnę do 5 000 zł.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  EmptyState,
  FormField,
  InfoBanner,
  Input,
  Modal,
  Select,
  Spinner,
  useToast,
} from '@k1p4/ui'
import { formatDate, formatPLN } from '@k1p4/i18n'
import { GARNISHMENT_KINDS, isEnforcementTitle, type GarnishmentKind } from '@k1p4/shared'
import { ApiError, api } from '../../api/client'
import styles from './FamilyMembers.module.css'

export interface GarnishmentsProps {
  employeeId: string
  /** Dodawanie i kończenie tytułów wymaga uprawnienia „Płace : Zatwierdzanie". */
  canEdit: boolean
}

interface AddForm {
  kind: GarnishmentKind | ''
  creditor: string
  caseNumber: string
  servedOn: string
  payeeName: string
  bailiffAccount: string
  totalDue: string
  monthlyDue: string
  effectiveFrom: string
  note: string
}

const EMPTY: AddForm = {
  kind: '',
  creditor: '',
  caseNumber: '',
  servedOn: '',
  payeeName: '',
  bailiffAccount: '',
  totalDue: '',
  monthlyDue: '',
  effectiveFrom: '',
  note: '',
}

/** Złote z formularza na grosze; puste pole to brak wartości, nie zero. */
function grosze(value: string): number | undefined {
  const normalized = value.replace(/\s/g, '').replace(',', '.')
  if (normalized === '') return undefined
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : undefined
}

export function Garnishments({ employeeId, canEdit }: GarnishmentsProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = {
    kind: useId(),
    creditor: useId(),
    caseNumber: useId(),
    account: useId(),
    totalDue: useId(),
    monthlyDue: useId(),
    from: useId(),
    note: useId(),
    endDate: useId(),
    payeeName: useId(),
    payeeAccount: useId(),
    servedOn: useId(),
    statementServedOn: useId(),
    obstacles: useId(),
  }

  const [addOpen, setAddOpen] = useState(false)
  const [form, setForm] = useState<AddForm>(EMPTY)
  /* `employer` — zaliczka: to koniec potrąceń, a nie uchylenie zajęcia (#12). */
  const [ending, setEnding] = useState<{ id: string; label: string; employer: boolean } | null>(
    null,
  )
  const [endDate, setEndDate] = useState('')
  /* Zmiana odbiorcy to nowy fakt z pisma, nie poprawka literówki (Etap 34). */
  const [payee, setPayee] = useState<{ id: string; label: string } | null>(null)
  const [payeeForm, setPayeeForm] = useState({ payeeName: '', bailiffAccount: '' })
  /* Oświadczenie z art. 882 § 1 k.p.c. (Etap 35). */
  const [statement, setStatement] = useState<{ id: string; label: string } | null>(null)
  const [statementForm, setStatementForm] = useState({ servedOn: '', obstaclesNote: '' })
  const [error, setError] = useState<string | null>(null)

  const orders = useQuery({
    queryKey: ['garnishments', employeeId],
    queryFn: () => api.garnishments(employeeId),
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['garnishments', employeeId] })
  }

  const add = useMutation({
    mutationFn: () =>
      api.addGarnishment(employeeId, {
        kind: form.kind as GarnishmentKind,
        creditor: form.creditor.trim(),
        ...(form.caseNumber.trim() ? { caseNumber: form.caseNumber.trim() } : {}),
        ...(form.servedOn ? { servedOn: form.servedOn } : {}),
        ...(form.payeeName.trim() ? { payeeName: form.payeeName.trim() } : {}),
        ...(form.bailiffAccount.trim() ? { bailiffAccount: form.bailiffAccount.trim() } : {}),
        ...(grosze(form.totalDue) !== undefined ? { totalDueGr: grosze(form.totalDue)! } : {}),
        ...(grosze(form.monthlyDue) !== undefined
          ? { monthlyDueGr: grosze(form.monthlyDue)! }
          : {}),
        effectiveFrom: form.effectiveFrom,
        ...(form.note.trim() ? { note: form.note.trim() } : {}),
      }),
    onSuccess: () => {
      toast.show(t('garnishments.added'), { tone: 'success' })
      setAddOpen(false)
      setForm(EMPTY)
      setError(null)
      refresh()
    },
    onError: (caught) => {
      setError(
        caught instanceof ApiError && caught.code === 'AMOUNT_REQUIRED'
          ? 'garnishments.errorAmount'
          : 'garnishments.errorGeneric',
      )
    },
  })

  const issueStatement = useMutation({
    mutationFn: () =>
      api.garnishmentStatement(statement!.id, {
        ...(statementForm.servedOn ? { servedOn: statementForm.servedOn } : {}),
        ...(statementForm.obstaclesNote.trim()
          ? { obstaclesNote: statementForm.obstaclesNote.trim() }
          : {}),
      }),
    onSuccess: (result) => {
      toast.show(t('garnishments.statementIssued', { date: result.deadline }), {
        tone: 'success',
      })
      setStatement(null)
      setStatementForm({ servedOn: '', obstaclesNote: '' })
      setError(null)
      refresh()
      /* Dokument wchodzi do akt — lista dokumentów musi to zobaczyć. */
      void queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: (caught) => {
      setError(
        caught instanceof ApiError && caught.code === 'SERVED_ON_REQUIRED'
          ? 'garnishments.errorServedOn'
          : caught instanceof ApiError && caught.code === 'NOT_ENFORCEMENT_TITLE'
            ? 'garnishments.errorNotEnforcement'
            : 'garnishments.errorGeneric',
      )
    },
  })

  const changePayee = useMutation({
    mutationFn: () =>
      api.setGarnishmentPayee(payee!.id, {
        ...(payeeForm.payeeName.trim() ? { payeeName: payeeForm.payeeName.trim() } : {}),
        bailiffAccount: payeeForm.bailiffAccount.trim(),
      }),
    onSuccess: () => {
      toast.show(t('garnishments.payeeSaved'), { tone: 'success' })
      setPayee(null)
      setPayeeForm({ payeeName: '', bailiffAccount: '' })
      setError(null)
      refresh()
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError && caught.code === 'NOT_ENFORCEMENT_TITLE'
          ? 'garnishments.errorNotEnforcement'
          : 'garnishments.errorGeneric',
      ),
  })

  const end = useMutation({
    mutationFn: () => api.endGarnishment(ending!.id, endDate),
    onSuccess: () => {
      toast.show(
        t(ending?.employer ? 'garnishments.endedDeductions' : 'garnishments.ended', {
          date: formatDate(endDate, 'dot'),
        }),
        { tone: 'success' },
      )
      setEnding(null)
      setEndDate('')
      setError(null)
      refresh()
    },
    onError: (caught) => {
      setError(
        caught instanceof ApiError && caught.code === 'BEFORE_START'
          ? ending?.employer
            ? 'garnishments.errorBeforeStartDeductions'
            : 'garnishments.errorBeforeStart'
          : 'garnishments.errorGeneric',
      )
    },
  })

  const items = orders.data?.items ?? []
  const ready =
    form.kind !== '' && form.creditor.trim() !== '' && form.effectiveFrom !== ''

  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <h3 className={styles.title}>{t('garnishments.title')}</h3>
        {canEdit ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setError(null)
              setAddOpen(true)
            }}
          >
            {t('garnishments.add')}
          </Button>
        ) : null}
      </div>
      <p className={styles.note}>{t('garnishments.lead')}</p>

      {error !== null && !addOpen && ending === null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      {orders.isPending ? (
        <Spinner label={t('garnishments.loading')} />
      ) : items.length === 0 ? (
        <EmptyState
          title={t('garnishments.empty')}
          description={t('garnishments.emptyBody')}
        />
      ) : (
        <Card pad="flush" className={styles.list}>
          {items.map((order) => (
            <div key={order.id} className={styles.row}>
              <span className={styles.rowName}>{t(`garnishments.kinds.${order.kind}`)}</span>
              <span className={styles.rowMeta}>
                {/* Wierzyciel kary to stała systemowa — w języku ekranu (fala 9, grupa 96). */}
                {order.kind === 'kara' ? t('garnishments.creditorPenalty') : order.creditor}
                {order.caseNumber ? ` · ${order.caseNumber}` : ''}
                {order.monthlyDueGr !== null
                  ? ` · ${t('garnishments.monthly', { amount: formatPLN(order.monthlyDueGr) })}`
                  : order.totalDueGr !== null
                    ? ` · ${t('garnishments.total', { amount: formatPLN(order.totalDueGr) })}`
                    : ''}
              </span>
              <span className={styles.rowDates}>
                {t('garnishments.from', { date: formatDate(order.effectiveFrom, 'dot') })}
                {order.effectiveTo
                  ? ` · ${t('garnishments.to', { date: formatDate(order.effectiveTo, 'dot') })}`
                  : ''}
                {/*
                  Dzień pierwszej wypłaty jest potrzebny do zawiadomienia
                  komornika z art. 881 § 3 pkt 1 k.p.c. — pismo pisze człowiek.
                */}
                {/*
                  Kara i zaliczka nie są zajęciem (#12): nie ma komornika,
                  odbiorcy przelewu ani terminu na oświadczenie — jest nota
                  z podstawą potrącenia.
                */}
                {!isEnforcementTitle(order.kind)
                  ? ` · ${t(
                      order.kind === 'kara'
                        ? 'garnishments.employerDeductionPenalty'
                        : 'garnishments.employerDeductionAdvance',
                    )}`
                  : order.firstTransferOn
                  ? ` · ${t('garnishments.firstTransfer', {
                      date: formatDate(order.firstTransferOn, 'dot'),
                    })}`
                  : order.bailiffAccount === null && order.effectiveTo === null
                    ? ` · ${t('garnishments.noPayeeAccount')}`
                    : ''}
                {/*
                  Termin z art. 882 § 1 k.p.c. Póki oświadczenia nie ma, mówimy
                  „do kiedy"; potem — kiedy wyszło. Bez daty doręczenia terminu
                  nie liczymy, tylko prosimy o datę.
                */}
                {!isEnforcementTitle(order.kind)
                  ? ''
                  : order.statementIssuedOn
                  ? ` · ${t('garnishments.statementDone', {
                      date: formatDate(order.statementIssuedOn, 'dot'),
                    })}`
                  : order.statementDeadline
                    ? ` · ${t('garnishments.statementDue', {
                        date: formatDate(order.statementDeadline, 'dot'),
                      })}`
                    : ` · ${t('garnishments.statementNoDate')}`}
              </span>
              {canEdit && isEnforcementTitle(order.kind) ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setError(null)
                    setStatementForm({ servedOn: order.servedOn ?? '', obstaclesNote: '' })
                    setStatement({
                      id: order.id,
                      label: `${t(`garnishments.kinds.${order.kind}`)} · ${order.kind === 'kara' ? t('garnishments.creditorPenalty') : order.creditor}`,
                    })
                  }}
                >
                  {t('garnishments.statement')}
                </Button>
              ) : null}
              {canEdit && order.effectiveTo === null && isEnforcementTitle(order.kind) ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setError(null)
                    setPayeeForm({
                      payeeName: order.payeeName ?? '',
                      bailiffAccount: order.bailiffAccount ?? '',
                    })
                    setPayee({
                      id: order.id,
                      label: `${t(`garnishments.kinds.${order.kind}`)} · ${order.kind === 'kara' ? t('garnishments.creditorPenalty') : order.creditor}`,
                    })
                  }}
                >
                  {t('garnishments.payee')}
                </Button>
              ) : null}
              {/*
                Karę kończy rejestr kar (sprzeciw, zatarcie) i jej saldo — ręczne
                „uchylenie" nie ma tu podstawy. Zaliczka kończy się zapisem
                końca potrąceń, a nie uchyleniem zajęcia (#12).
              */}
              {canEdit && order.effectiveTo === null && order.kind !== 'kara' ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setError(null)
                    setEnding({
                      id: order.id,
                      label: `${t(`garnishments.kinds.${order.kind}`)} · ${order.kind === 'kara' ? t('garnishments.creditorPenalty') : order.creditor}`,
                      employer: !isEnforcementTitle(order.kind),
                    })
                  }}
                >
                  {t(isEnforcementTitle(order.kind) ? 'garnishments.end' : 'garnishments.endDeductions')}
                </Button>
              ) : null}
            </div>
          ))}
        </Card>
      )}

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title={t('garnishments.formTitle')}>
        <p className={styles.modalLead}>{t('garnishments.formLead')}</p>
        {error !== null ? (
          <InfoBanner tone="danger" className={styles.banner}>
            {t(error)}
          </InfoBanner>
        ) : null}

        <div className={styles.formGrid}>
          {/*
            Rodzaj decyduje o granicy i o kwocie wolnej — dlatego lista z ustawy,
            a nie wolny tekst (art. 87 § 1 k.p.).
          */}
          <FormField label={t('garnishments.fields.kind')} htmlFor={ids.kind}>
            <Select
              id={ids.kind}
              value={form.kind}
              onChange={(event) =>
                setForm({ ...form, kind: event.target.value as GarnishmentKind | '' })
              }
            >
              <option value="">{t('garnishments.fields.kindPlaceholder')}</option>
              {GARNISHMENT_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`garnishments.kinds.${kind}`)}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label={t('garnishments.fields.creditor')} htmlFor={ids.creditor}>
            <Input
              id={ids.creditor}
              value={form.creditor}
              onChange={(event) => setForm({ ...form, creditor: event.target.value })}
            />
          </FormField>
          <FormField label={t('garnishments.fields.caseNumber')} htmlFor={ids.caseNumber}>
            <Input
              id={ids.caseNumber}
              value={form.caseNumber}
              onChange={(event) => setForm({ ...form, caseNumber: event.target.value })}
            />
          </FormField>
          {/*
            Odbiorca przelewu: art. 881 § 3 k.p.c. każe przekazywać zajęte kwoty
            wprost wierzycielowi egzekwującemu albo komornikowi — o tym, komu,
            rozstrzyga wezwanie, nie my.
          */}
          <FormField
            label={t('garnishments.fields.payeeName')}
            htmlFor={ids.payeeName}
            hint={t('garnishments.fields.payeeNameHint')}
          >
            <Input
              id={ids.payeeName}
              value={form.payeeName}
              onChange={(event) => setForm({ ...form, payeeName: event.target.value })}
            />
          </FormField>
          <FormField
            label={t('garnishments.fields.bailiffAccount')}
            htmlFor={ids.account}
            hint={t('garnishments.fields.bailiffAccountHint')}
          >
            <Input
              id={ids.account}
              value={form.bailiffAccount}
              onChange={(event) => setForm({ ...form, bailiffAccount: event.target.value })}
            />
          </FormField>
          <FormField
            label={t('garnishments.fields.totalDue')}
            htmlFor={ids.totalDue}
            hint={t('garnishments.fields.totalDueHint')}
          >
            <Input
              id={ids.totalDue}
              inputMode="decimal"
              value={form.totalDue}
              onChange={(event) => setForm({ ...form, totalDue: event.target.value })}
            />
          </FormField>
          <FormField
            label={t('garnishments.fields.monthlyDue')}
            htmlFor={ids.monthlyDue}
            hint={t('garnishments.fields.monthlyDueHint')}
          >
            <Input
              id={ids.monthlyDue}
              inputMode="decimal"
              value={form.monthlyDue}
              onChange={(event) => setForm({ ...form, monthlyDue: event.target.value })}
            />
          </FormField>
          {/*
            Dzień doręczenia wezwania to inna data niż początek zajęcia: od niego
            biegnie tydzień na oświadczenie z art. 882 § 1 k.p.c.
          */}
          <FormField
            label={t('garnishments.fields.servedOn')}
            htmlFor={ids.servedOn}
            hint={t('garnishments.fields.servedOnHint')}
          >
            <Input
              id={ids.servedOn}
              type="date"
              value={form.servedOn}
              onChange={(event) => setForm({ ...form, servedOn: event.target.value })}
            />
          </FormField>
          <FormField label={t('garnishments.fields.effectiveFrom')} htmlFor={ids.from}>
            <Input
              id={ids.from}
              type="date"
              value={form.effectiveFrom}
              onChange={(event) => setForm({ ...form, effectiveFrom: event.target.value })}
            />
          </FormField>
          <FormField label={t('garnishments.fields.note')} htmlFor={ids.note}>
            <Input
              id={ids.note}
              value={form.note}
              onChange={(event) => setForm({ ...form, note: event.target.value })}
            />
          </FormField>
        </div>

        <div className={styles.actions}>
          <Button variant="secondary" onClick={() => setAddOpen(false)}>
            {t('garnishments.cancel')}
          </Button>
          <Button disabled={!ready || add.isPending} onClick={() => add.mutate()}>
            {add.isPending ? <Spinner label={t('garnishments.saving')} /> : t('garnishments.submit')}
          </Button>
        </div>
      </Modal>

      <Modal
        open={statement !== null}
        onClose={() => setStatement(null)}
        title={t('garnishments.statementTitle', { document: statement?.label ?? '' })}
      >
        <p className={styles.modalLead}>{t('garnishments.statementLead')}</p>
        {error !== null ? (
          <InfoBanner tone="danger" className={styles.banner}>
            {t(error)}
          </InfoBanner>
        ) : null}
        <div className={styles.formGrid}>
          <FormField
            label={t('garnishments.fields.servedOn')}
            htmlFor={ids.statementServedOn}
            hint={t('garnishments.fields.servedOnHint')}
          >
            <Input
              id={ids.statementServedOn}
              type="date"
              value={statementForm.servedOn}
              onChange={(event) =>
                setStatementForm({ ...statementForm, servedOn: event.target.value })
              }
            />
          </FormField>
          {/*
            Spraw sądowych o zajęte wynagrodzenie system nie zna — art. 882 § 1
            pkt 3 każe je zgłosić, więc wpisuje je człowiek. Puste pole znaczy,
            że pracodawca innych przeszkód nie zgłasza.
          */}
          <FormField
            label={t('garnishments.fields.obstacles')}
            htmlFor={ids.obstacles}
            hint={t('garnishments.fields.obstaclesHint')}
          >
            <Input
              id={ids.obstacles}
              value={statementForm.obstaclesNote}
              onChange={(event) =>
                setStatementForm({ ...statementForm, obstaclesNote: event.target.value })
              }
            />
          </FormField>
        </div>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={() => setStatement(null)}>
            {t('garnishments.cancel')}
          </Button>
          <Button
            disabled={statementForm.servedOn === '' || issueStatement.isPending}
            onClick={() => issueStatement.mutate()}
          >
            {issueStatement.isPending ? (
              <Spinner label={t('garnishments.saving')} />
            ) : (
              t('garnishments.statementSubmit')
            )}
          </Button>
        </div>
      </Modal>

      <Modal
        open={payee !== null}
        onClose={() => setPayee(null)}
        title={t('garnishments.payeeTitle', { document: payee?.label ?? '' })}
      >
        <p className={styles.modalLead}>{t('garnishments.payeeLead')}</p>
        {error !== null ? (
          <InfoBanner tone="danger" className={styles.banner}>
            {t(error)}
          </InfoBanner>
        ) : null}
        <div className={styles.formGrid}>
          <FormField
            label={t('garnishments.fields.payeeName')}
            htmlFor={ids.payeeName}
            hint={t('garnishments.fields.payeeNameHint')}
          >
            <Input
              id={ids.payeeName}
              value={payeeForm.payeeName}
              onChange={(event) => setPayeeForm({ ...payeeForm, payeeName: event.target.value })}
            />
          </FormField>
          <FormField
            label={t('garnishments.fields.bailiffAccount')}
            htmlFor={ids.payeeAccount}
            hint={t('garnishments.fields.bailiffAccountHint')}
          >
            <Input
              id={ids.payeeAccount}
              value={payeeForm.bailiffAccount}
              onChange={(event) =>
                setPayeeForm({ ...payeeForm, bailiffAccount: event.target.value })
              }
            />
          </FormField>
        </div>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={() => setPayee(null)}>
            {t('garnishments.cancel')}
          </Button>
          <Button
            disabled={payeeForm.bailiffAccount.trim() === '' || changePayee.isPending}
            onClick={() => changePayee.mutate()}
          >
            {changePayee.isPending ? (
              <Spinner label={t('garnishments.saving')} />
            ) : (
              t('garnishments.submit')
            )}
          </Button>
        </div>
      </Modal>

      <Modal
        open={ending !== null}
        onClose={() => setEnding(null)}
        title={t(ending?.employer ? 'garnishments.endDeductionsTitle' : 'garnishments.endTitle', {
          document: ending?.label ?? '',
        })}
      >
        <p className={styles.modalLead}>
          {t(ending?.employer ? 'garnishments.endDeductionsLead' : 'garnishments.endLead')}
        </p>
        {error !== null ? (
          <InfoBanner tone="danger" className={styles.banner}>
            {t(error)}
          </InfoBanner>
        ) : null}
        <div className={styles.formGrid}>
          <FormField
            label={t(ending?.employer ? 'garnishments.fields.deductionsTo' : 'garnishments.fields.effectiveTo')}
            htmlFor={ids.endDate}
          >
            <Input
              id={ids.endDate}
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </FormField>
        </div>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={() => setEnding(null)}>
            {t('garnishments.cancel')}
          </Button>
          <Button disabled={endDate === '' || end.isPending} onClick={() => end.mutate()}>
            {end.isPending ? (
              <Spinner label={t('garnishments.saving')} />
            ) : (
              t(ending?.employer ? 'garnishments.endDeductionsSubmit' : 'garnishments.endSubmit')
            )}
          </Button>
        </div>
      </Modal>
    </section>
  )
}
